import { DataGridColumn, DataType, SourceType, QueryFilter } from "../../../models/queries/queries.models";
import { IActive } from "../../../models/people/people.models";
import { I18N } from "aurelia-i18n";
import { autoinject, observable, computedFrom, BindingEngine } from "aurelia-framework";
import { GenesisLookups } from "../../../constants/lookups/lookup.constants";
import { LookupValueRequest, LookupValue } from "../../../models/lookups/lookup.models";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { NotificationType, NotificationClientService } from "../../../services/basics/notification-client.service";
import { UserClientService } from "../../../services/authentication/user-client.service";
import * as Enumerable from "linq";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { PersistRightShipQiUserRequest } from "../../../models/customers/customer.models";
import { ManageRightShipUsersClientService } from "../../../services/modules/righship-qi-manage-users.service";
import { RightShipDialogServiceConstants } from "../../../constants/basics/rightship-dialog.constants";
import { MenuItem } from "../../components/menu/menu-item";
import { RightshipConfirmationDialogModel } from "../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { Claims } from "../../../constants/authentication/claims";
import { LookupValueQuery } from "../../../constants/modules/lookup.constants";
import { CustomGridPageBase } from "../common/custom-grid-page.base";
import { Guid } from "../../../utilities/guid";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { SignalFunctions } from "../../../services/basics/signal-functions";
import { MobileHelper } from "../../../helpers/mobile-helper";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Reflection } from "../../../utilities/reflection";

@autoinject()
export class ViewPeople extends CustomGridPageBase {

    public customerId: number;
    public selectedCustomerId: number;
    public selectedBusinessUnitItems: number[];
    public branchIdFilter: number;
    public readOnlyRoles: boolean = true;
    public canAddPerson: boolean = false;
    public canExportPerson: boolean = false;
    public canEditPerson: boolean = false;
    public canDeletePerson: boolean = false;
    public isMobileFilterOpen: boolean = false;
    public selectedRoleIds: number[] = [];
    public selectedStatusIds: number[] = [];
    public initialFilters: QueryFilter[];
    public canMoveUsers: boolean;
    public canBulkUpdateUsers: boolean;
    public canResetMfaEnrollment: boolean;
    public showList: boolean = true;
    public isDataSubmission = false;
    public showAllCustomers = false;
    public canViewUserAudits: boolean;
    public showColumnSorting: boolean;
    public showBUFilter: boolean;
    public showRoleFilter: boolean;
    public showStatusFilter: boolean;
    public selectedFilter: { title: string, clear: () => void, apply: () => void };
    public selectedColumnSorting: number[] = [];

    private _customerNote?: string;

    @observable()
    public searchText: string;

    statuses: IActive[] = [
        { id: 1, name: 'Is Active', value: true },
        { id: 2, name: 'Not Active', value: false },
    ];

    @computedFrom(Reflection.getPropertyPath<ViewPeople>(z => z.showBUFilter),
        Reflection.getPropertyPath<ViewPeople>(z => z.showRoleFilter),
        Reflection.getPropertyPath<ViewPeople>(z => z.showStatusFilter),
        Reflection.getPropertyPath<ViewPeople>(z => z.showColumnSorting))
    public get mobileFilterSelected() {
        return this.showBUFilter || this.showRoleFilter || this.showStatusFilter || this.showColumnSorting;
    }

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }

    mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();
    }

    // Get DataGrid columns
    private getColumns(): DataGridColumn[] {
        return [
            //Checkbox
            {
                dataType: DataType.CustomTemplate,
                value: `<check-box checked.bind="getRowItem() && getRowItem().isSelected"
                            changed.delegate="getDataGrid().selectRowInMultipleSelection(getRowItem())">
                        </check-box>`,
                name: "select",
                title: MobileHelper.isMobileDevice() ? "" : `<check-box class="check-box-select-all" checked.two-way="toggleAll"></check-box>`,
                parameter: {
                    item: null
                },
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -2,
                width: "50px",
                isHidden: false,
                isSortable: false,
                getCellClass: () => {
                    return "selector-checkbox";
                }
            },
            //Name
            {
                sortOrder: 1,
                columnOrder: 1,
                name: 'fullName',
                dataType: DataType.Default,
                title: this.i18N.tr('name-text'),
                isSortable: true,
            },
            //Email
            {
                sortOrder: 2,
                columnOrder: 2,
                name: 'email',
                dataType: DataType.Default,
                title: this.i18N.tr('email-text'),
                isSortable: true,
            },
            //Phone number
            {
                sortOrder: 3,
                columnOrder: 3,
                name: 'phoneNumber',
                dataType: DataType.Default,
                title: this.i18N.tr('phone-text'),
                readOnly: true,
                isSortable: true,
            },
            //Customer
            {
                sortOrder: 4,
                columnOrder: 4,
                name: 'customerName',
                dataType: DataType.CustomTemplate,
                title: 'Customer',
                instancePerRow: true,
                readOnly: true,
                isHidden: !this.showAllCustomers,
                value: '<a class="link" if.bind="item.customerId" href="customer/${item.customerId}?tabStep=6">${item.customerName}</a>',
                isSortable: true,
                parameter: {
                    item: null
                }
            },
            //Business unit
            {
                sortOrder: 4,
                columnOrder: 4,
                name: 'businessUnitName',
                dataType: DataType.Default,
                title: this.i18N.tr('business-unit-title-text'),
                readOnly: true,
                isHidden: !this.showAllCustomers && (!this.customerId || this.isDataSubmission),
                isSortable: true
            },
            //Business unit access
            {
                sortOrder: 5,
                columnOrder: 5,
                name: 'businessUnitAccess',
                dataType: DataType.Default,
                title: this.i18N.tr('business-unit-access-text'),
                readOnly: true,
                isHidden: !this.showAllCustomers && (!this.customerId || this.isDataSubmission),
                isSortable: true
            },
            //Role
            {
                sortOrder: 6,
                columnOrder: 6,
                name: 'roleName',
                dataType: DataType.Default,
                title: this.i18N.tr('role-text'),
                readOnly: false,
                isHidden: this.isDataSubmission,
                isSortable: true
            },
            //Authorised to amend vetting rules
            {
                sortOrder: 7,
                columnOrder: 7,
                name: 'canAmmendVetRules',
                dataType: DataType.Boolean,
                title: this.i18N.tr('can-ammend-vet-rules-text'),
                readOnly: true,
                isHidden: this.isDataSubmission,
                isSortable: true
            },
            //Company
            {
                sortOrder: 7,
                columnOrder: 7,
                name: 'company',
                dataType: DataType.Default,
                title: 'Company',
                readOnly: true,
                isHidden: !this.isDataSubmission,
                isSortable: true
            },
            //Is active
            {
                sortOrder: 8,
                columnOrder: 8,
                name: 'isActive',
                dataType: DataType.Boolean,
                title: this.i18N.tr('is-active-text'),
                readOnly: true,
                isSortable: true
            },
            //Is mfa excluded
            {
                sortOrder: 9,
                columnOrder: 9,
                name: 'isUserMfaExcluded',
                dataType: DataType.Boolean,
                title: this.i18N.tr('is-mfa-excluded-text'),
                readOnly: true,
                isSortable: true
            },
            //Menu (more)
            {
                columnOrder: 99,
                sortOrder: 99,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                title: "More",
                isSortable: false,
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" if.bind="menuItems(item).length > 0">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    menuItems: (item) =>
                    {
                        const menuItems: MenuItem[] = [];

                        if (this.canEditPerson) {
                            menuItems.push({isVisible: true, click: () => this.editUser(item), item: { text: "Edit" }, classList: ''});
                            menuItems.push({isVisible: true, click: () => this.toggleUserActivity([item]), item: { text: item.isActive? "Deactivate": "Activate" }, classList: ''});
                        }
                        
                        if(this.canMoveUsers) {
                            menuItems.push({isVisible: true, click: () => this.movePersonToCustomer([item]), item: {text: 'Move'}, classList: ''});
                        }

                        if (this.canDeletePerson) {
                            menuItems.push({isVisible: true, click: () => this.showDeleteDialog([item]), item: { text: "Remove" }, classList: ''});
                        }
                
                        if (this.canAddPerson) {
                            menuItems.push({isVisible: !this.isDataSubmission, click: () => this.sendInvite(item.userId, item.hasLoggedIn), item: { text: "Resend invite" }, classList: ''});
                        }
                        
                        if (this.canResetMfaEnrollment && item.useCustomerMfa) {
                            menuItems.push({isVisible: !this.isDataSubmission, click: () => this.resetUserMfaEnrollment(item.userId), item: { text: "Reset MFA enrollment" }, classList: ''});
                        }

                        if(this.canViewUserAudits) {
                            menuItems.push({isVisible: true, click: () => this.showUserAuditDialog(item.userId), item: { text: "Audit log"}, classList: ''});
                        }

                        return menuItems;
                    }
                }
            }
        ];
    }

    @observable()
    public roles: LookupValue[] = [];

    @computedFrom("selectedBusinessUnitItems", "selectedBusinessUnitItems.length")
    public get buFilterActive() {
        return this.selectedBusinessUnitItems?.length > 0;
    }

    @computedFrom("selectedRoleItems", "selectedRoleItems.length")
    public get roleFilterActive() {
        return this.selectedRoleItems?.length > 0;
    }

    @computedFrom("selectedStatusItems", "selectedStatusItems.length")
    public get statusFilterActive() {
        return this.selectedStatusItems?.length > 0;
    }

    @computedFrom("selectedRoleIds")
    public get selectedRoleItems(): LookupValue[] {
        return this.roles.filter(r => this.selectedRoleIds.some(id => r.id == id));
    }

    @computedFrom("selectedStatusIds")
    public get selectedStatusItems(): IActive[] {
        return this.statuses.filter(r => this.selectedStatusIds.some(id => r.id == id));
    }

    @computedFrom('selectedBusinessUnitItems', 'selectedRoleItems', 'selectedStatusItems')
    public get areFiltersSelected(){
        return this.selectedBusinessUnitItems?.length || this.selectedRoleItems?.length || this.selectedStatusItems?.length;
    }

    public set selectedStatusItems(value: IActive[]) {
        const ids = value.map(r => r.id);
        const hasChanges = ids.length != this.selectedStatusIds.length
            || this.selectedStatusIds.length - Enumerable.from(this.selectedStatusIds).intersect(ids).count() > 0;

        if (!hasChanges) {
            return;
        }

        this.selectedStatusIds = ids;
    }

    activate(params) {
        this.showAllCustomers = params.showAllCustomers ? true : false;

        this.branchIdFilter = this.showAllCustomers ? null : (params.branchId ? params.branchId : this.userClientService.currentUser.branchId);
        this.customerId = this.showAllCustomers ? null : params.customerId;

        this.selectedCustomerId = null;
        /** Returns status to default value. */
        this.selectedStatusIds = [];
        this.selectedStatusIds.push(this.statuses.filter(x => x.value)[0].id);

        if (params?.customer) {
            this.isDataSubmission = params.customer.isDataSubmission;
            this._customerNote = params.customer.customerNote;
        }

        if (params?.readonlyRoles == false) {
            this.readOnlyRoles = false;
        }

        if (this.branchIdFilter) {
            const departmentRequest = new LookupValueRequest();
            departmentRequest.lookupValueQuery = "departments";
            departmentRequest.parameters = this.branchIdFilter ? this.branchIdFilter.toString() : "";

            this.roles = [];
            if(departmentRequest.parameters){
                this.lookupsClientService.getLookup(departmentRequest, 'default').then(x => {
                    const departments = x.activeValues;

                    if (departments && departments.length > 0) {
                        const roleRequest = new LookupValueRequest();
                        roleRequest.lookupValueQuery = "roles";
                        roleRequest.parameters = departments[0].id.toString();

                        if(roleRequest)
                        this.lookupsClientService.getLookup(roleRequest, 'default', 0).then(x => {
                            this.roles = x.activeValues;
                        });
                    }
                });
            }

            this.canAddPerson = params.canAddPerson; 
            this.canEditPerson = params.canEditPerson;
        }

        this.setPeopleItemSource();
    }

    public set selectedRoleItems(value: LookupValue[]) {
        const ids = value.map(r => r.id);
        const hasChanges = ids.length != this.selectedRoleIds.length
            || this.selectedRoleIds.length - Enumerable.from(this.selectedRoleIds).intersect(ids).count() > 0;

        if (!hasChanges) {
            return;
        }

        this.selectedRoleIds = ids;
    }

    constructor(protected i18N: I18N, protected bindingEngine: BindingEngine, private lookupsClientService: LookupClientService,
        private manageUsersClientService: ManageRightShipUsersClientService, private notificationClientService: NotificationClientService,
        private userClientService: UserClientService, private userClaimsService: UserClaimsService,
        private dialogClientService: DialogClientService, private signalClientService: SignalClientService) {
        super(i18N, 'People', bindingEngine)
        this.canExportPerson = this.userClaimsService.hasPermission(Claims.CanExportPeople)
        this.canDeletePerson = this.userClaimsService.hasPermission(Claims.CanDeletePeople);
        this.canMoveUsers = this.userClaimsService.hasPermission(Claims.CanMoveUsers);
        this.canBulkUpdateUsers = this.userClaimsService.hasPermission(Claims.CanBulkUpdateUsers);
        this.canResetMfaEnrollment = this.userClaimsService.hasPermission(Claims.CanResetMfaEnrollment);
        this.canViewUserAudits = true;
    }

    attached() {
        this.signalClientService.subscribe(SignalFunctions.peopleChanged, this.reloadData);
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.peopleChanged, this.reloadData);
    }

    //Update the data-grid's item-source
    private setPeopleItemSource() {

        const url = 'odata/RightShipQiUsers';
        const filters: QueryFilter[] = [];

        if (this.branchIdFilter) {
            filters.push(QueryFilter.getQuery("branchId", this.branchIdFilter, 'eq', DataType.Number));
        }

        if (this.searchText) {
            const searchGuid = Guid.generateGuid();

            filters.push(QueryFilter.getQuery("fullName", this.searchText, "contains({0}, '{1}')", DataType.Default, true, false, searchGuid, null, true));
            filters.push(QueryFilter.getQuery("email", this.searchText, "contains({0}, '{1}')", DataType.Default, false, false, searchGuid, null, true));
        }

        if (this.selectedBusinessUnitItems?.length > 0) {
            const filter = QueryFilter.getQuery("businessUnitId", this.selectedBusinessUnitItems.map(x => x.toString()).join(","),
                '{0} in ({1})', DataType.Lookup, true, false, null, null, true);
            filter.filterProperty.lookupMappedToProperty = LookupValueQuery.BusinessUnits;
            filters.push(filter);
        }

        if (this.selectedRoleIds?.length > 0) {
            const filter = QueryFilter.getQuery("roleId", this.selectedRoleIds.map(x => x.toString()).join(","),
                '{0} in ({1})', DataType.Lookup, true, false, null, null, true);
            filter.filterProperty.lookupMappedToProperty = GenesisLookups.roles;
            filters.push(filter);
        }

        if (this.selectedStatusIds?.length > 0) {
            const selectedStatuses = this.statuses.filter(x => this.selectedStatusIds.indexOf(x.id) > -1);
            const groupguid = Guid.generateGuid();
            let check = false;
            if(selectedStatuses.filter(x => x.value == true).length > 0)
            {
                const filter = QueryFilter.getQuery("isActive", true,
                    'eq', DataType.Boolean, true, false, groupguid, null, false);
                filters.push(filter);
                check = true;
            }

            if(selectedStatuses.filter(x => x.value == false).length > 0)
            {
                const filter = QueryFilter.getQuery("isActive", false,
                    'eq', DataType.Boolean, !check, true, groupguid, null, false);
                filters.push(filter);
            }
        }

        this.initialFilters = filters;

        this.itemSource = {
            source: url,
            initialFilters: filters,
            sourceType: SourceType.ODataUrl,
            columns: this.getColumns(),
        };
    }

    //Toggle Business Unit filters
    public toggleBUFilters() {
        if (!this.showBUFilter) {
            this.closeAllFilters();
        }
        this.showBUFilter = !this.showBUFilter;
        this.selectedFilter = {
            title: "Business Unit",
            clear: () => {
                this.clearBUFilter();
            },
            apply: () => {
                this.applyBUFilter(this.selectedBusinessUnitItems.map(bId => {return {id: bId}}));
            }
        }
    }

    //Toggle customer roles filters
    public toggleRoleFilters() {
        if (!this.showRoleFilter) {
            this.closeAllFilters();
        }
        this.showRoleFilter = !this.showRoleFilter;
        this.selectedFilter = {
            title: "Role",
            clear: () => {
                this.clearRoleFilter();
            },
            apply: () => {
                this.applyRoleFilter(this.selectedRoleIds.map(rId => {return {id: rId}}));
            }
        }
    }

    //Toggle active status filters
    public toggleStatusFilters() {
        if (!this.showStatusFilter) {
            this.closeAllFilters();
        }
        this.showStatusFilter = !this.showStatusFilter;
        this.selectedFilter = {
            title: "Status",
            clear: () => {},
            apply: () => {
                this.applyStatusFilter(this.selectedStatusIds.map((sId: number) => {return {id: sId}}));
            }
        }
    }

    public toggleColumnSortingFilters(){
        if (!this.showColumnSorting) {
            this.closeAllFilters();
        }
        this.showColumnSorting = !this.showColumnSorting;
        this.selectedFilter = {
            title: "Column sorting",
            clear: () => {
                this.clearBUFilter();
            },
            apply: () => {
                this.applyColumnSortingFilter(this.selectedColumnSorting.map((bId: number) => {
                    return { id: bId };
                }), false);
            }
        }
    }

    applyBUFilter(event, reload: boolean = true) {
        this.selectedBusinessUnitItems = event.map(x => x.id);
        if (reload == true) this.setPeopleItemSource();
        this.showBUFilter = false;
    }

    applyRoleFilter(event, reload: boolean = true) {
        this.selectedRoleIds = event.map(x => x.id);
        if (reload == true) this.setPeopleItemSource();
        this.showRoleFilter = false;
    }

    applyStatusFilter(event, reload: boolean = true) {
        this.selectedStatusIds = event.map(x => x.id);
        if (reload == true) this.setPeopleItemSource();
        this.showStatusFilter = false;
    }

    public clearBUFilter() {
        this.selectedBusinessUnitItems = [];
        this.setPeopleItemSource();
    }

    public clearRoleFilter() {
        this.selectedRoleItems = [];
        this.setPeopleItemSource();
    }

    public clearStatusFilter() {
        this.selectedStatusItems = [];
        this.setPeopleItemSource();
    }

    //Close all expandable filters
    public closeAllFilters() {
        this.showColumnSorting = false;
        this.showBUFilter = false;
        this.showRoleFilter = false;
        this.showStatusFilter = false;
    }

    public sortByMobile(column: DataGridColumn){
        this.dataGridVM.sortingTriggered(column);
    }

    public sortClass(column: DataGridColumn) {
        if (column.isAscending === true) {
            return "Ascending"
        }
        else if (column.isAscending === false) {
            return "Descending"
        }
        else {
            return "";
        }
    }

    addPerson() {
        const customerId = this.customerId;
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ManagePersonDialog, {
            userId: 0,
            customerId: customerId,
            branchId: this.branchIdFilter,
            banner: this._customerNote,
            reloadData: () => this.dataGridVM.reloadData()
        }, true, false, false, 'right');
    }

    editUser = (item) => {
        if (this.showList) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ManagePersonDialog, {
                userId: item.userId,
                customerId: this.customerId ? this.customerId : item.customerId,
                branchId: this.branchIdFilter ? this.branchIdFilter : item.branchId,
                banner: this._customerNote,
                reloadData: () => this.dataGridVM.reloadData()
            }, true, false, false, 'right');
        }
    }

    bulkChangeRole() {
        if(this.shouldDisableBulkChangeRole) return;
        let userIds;

        //If toggle all is selected, get all selected users, else get specific ids which is selected
        if(this.dataGridVM.toggleAll) {
            if(this.dataGridVM.getSelectedRowsCollection().length > 0) {
                userIds = this.dataGridVM.getSelectedRowsCollection().map(x => x.item.userId);
            } else {
                userIds = this.dataGridVM.itemsCollection.map(x => x.item.userId);
            }
        }
        else {
            userIds = this.dataGridVM.getSpecificIds();
        }
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ManagePersonDialog, {
            userIds: userIds,
            branchId: this.branchIdFilter,
            reloadData: () => this.dataGridVM.reloadData()
        }, true, false, false, 'right');
    }

    reloadData = (param) => {
        if (this.dataGridVM) {
            if (param.customerId == this.customerId || !this.customerId) {
                this.dataGridVM.reloadData();
            }
        }
    }

    toggleUserActivity(users) {
        const updateActivity = () => {
            this.manageUsersClientService.updateUserActivity({ ids: users.map(x => x.userId), activate: activateUser });
            this.notificationClientService.show((activateUser ? "Activation": "Deactivation") + " will be done in the background", "", NotificationType.Success);
        }

        const activateUser = (users[0].isActive ? false: true);

        if (users.filter(x => !x.hasUserLogins)[0] && activateUser) {
            const model = new RightshipConfirmationDialogModel();
            model.confirm = this.i18N.tr('submit-text');
            model.cancel = this.i18N.tr('cancel-text');
            model.onConfirm = () => updateActivity();
            model.messages.push('An invite would be sent with an auto-generated password');
            model.title = 'Are you sure you want to proceed?';
    
            this.dialogClientService.hide();

            setTimeout(() => {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, true, false);
            });
        }
        else {
            updateActivity();
        }
    }

    showDeleteDialog = (users: any) => {
        const model = new RightshipConfirmationDialogModel();
        model.confirm = "Yes, Remove user";
        model.cancel = this.i18N.tr('cancel-text');
        model.onConfirm = () => this.deleteUsers(users.map(x => x.userId));
        model.confirmClass = 'danger';

        model.messageClasses.push('text-bold');

        if(this.customerId) {
            model.messages.push('Selected user(s) will be removed. They will lose access to everything in the company.');
        }
        else {
            model.messages.push('Selected user(s) will be removed. They will lose access to everything.');
        }

        model.title = 'Confirm remove user(s)'

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, true, false);
    }

    deleteUsers = (userIds: number[]) => {
        return new Promise<void>((resolve) => {
            this.manageUsersClientService.delete(userIds);
            this.notificationClientService.show("Deletion will be done in the background", "", NotificationType.Success);
            resolve();
        })
    }

    resetUserMfaEnrollment = (userId: number) => {
        this.manageUsersClientService.resetUserMfaEnrollment(userId).then(() => {            
            this.notificationClientService.show("Reset MFA Enrollment Successfully", '', NotificationType.Success);
        });
    }

    sendInvite = (userId: number, hasLoggedIn: boolean) => {
        const inviteFunc = () => {
            this.manageUsersClientService.getUser(userId).then(x => {

                const request: PersistRightShipQiUserRequest = {
                    changes: {},
                    id: userId,
                    newPassword: null,
                    passwordConfirmation: null,
                    businessUnitId: x.businessUnit ? x.businessUnit.businessUnitId : null,
                    accessableBusinessUnitIds: x.businessUnitUserPermissions.map(x => x.businessUnitId),
                    resendInvite: true,
                    userCompanyAssociations: null,
                    isUserMfaExcluded: x.isUserMfaExcluded
                }
    
                this.manageUsersClientService.save(request).then(() => {
                    this.notificationClientService.show(this.i18N.tr('user-invited-text'), "", NotificationType.Success);
                });
            });
        }

        if (hasLoggedIn) {
            const model: RightshipConfirmationDialogModel = {
                title: "Resend Invite",
                messages: 
                    [
                        "This user has already successfully logged in.",
                        "Are you sure you want to re-invite this user? The password would be reset."
                    ],
                cancel: "Cancel",
                confirm: "Yes, Resend invite",
                onConfirm: inviteFunc
            };
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, false, false);
        }
        else {
            inviteFunc();
        }
    }

    public get isSubFilterOpen(): boolean {
        return this.showBUFilter || this.showRoleFilter || this.showStatusFilter || this.showColumnSorting;
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.showColumnSorting = false;
        this.showBUFilter = false;
        this.showRoleFilter = false;
        this.showStatusFilter = false;
    }

    clearNameFilter() {
        this.searchText = null;
    }

    //When the 'Search name / email' field is updated
    searchTextChanged() {
        this.setPeopleItemSource();
    }

    clearAllFilters(reload: boolean = true) {
        this.selectedBusinessUnitItems = [];
        this.selectedRoleItems = [];
        this.selectedStatusIds = [];

        if (reload)
            this.setPeopleItemSource();
    }

    movePersonToCustomer(items) {
        const model = {
            customerId: this.customerId ?? items[0].customerId,
            roleId: items[0].roleId, 
            businessUnitId: items[0].businessUnitId,
            userIds: items.map(x => x.userId),
            branchId: items[0].branchId,
            onMoved: () => this.dataGridVM.reloadData()
        }
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.MoveUserDialog, model, true, false, false, 'right');
    }

    @computedFrom(Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM),
        Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM.itemsCollection),
        Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM.itemsCollection.length),
        "dataGridVM._specificIds", "dataGridVM._specificIds.length")
    get shouldDisableActivate(): boolean {
        if(!this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection()) {
            return false;
        }
        else {
            if(this.dataGridVM.getSelectedRowsCollection().length > 0) {
                return !this.dataGridVM.getSelectedRowsCollection().map(x => x.item).every(x => x.isActive === false);
            }

            return !this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection() || this.dataGridVM.getSelectedRowsCollection().length <= 0;
        }
    }

    @computedFrom(Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM),
        Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM.itemsCollection),
        Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM.itemsCollection.length),
        "dataGridVM._specificIds", "dataGridVM._specificIds.length")
    get shouldDisableDeactivate(): boolean {
        if(!this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection()) {
            return false;
        }
        else {
            if(this.dataGridVM.getSelectedRowsCollection().length > 0) {
                return !this.dataGridVM.getSelectedRowsCollection().map(x => x.item).every(x => x.isActive === true);
            }

            return !this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection() || this.dataGridVM.getSelectedRowsCollection().length <= 0;
        }
    }

    @computedFrom(Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM),
        Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM.itemsCollection),
        Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM.itemsCollection.length),
        Reflection.getPropertyPath<ViewPeople>(z => z.canMoveUsers),
        "dataGridVM._specificIds", "dataGridVM._specificIds.length")
    get shouldDisableMove(): boolean {
        if (!this.canMoveUsers) {
            return true;
        }

        if(!this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection()) {
            return false;
        }
        else {
            if(this.dataGridVM.getSelectedRowsCollection().length > 0) {
                return false;
            }

            return !this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection() || this.dataGridVM.getSelectedRowsCollection().length <= 0;
        }
    }

    @computedFrom(Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM),
        Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM.itemsCollection),
        Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM.itemsCollection.length),
        Reflection.getPropertyPath<ViewPeople>(z => z.canDeletePerson),
        "dataGridVM._specificIds", "dataGridVM._specificIds.length")
    get shouldDisableDelete(): boolean {
        if (!this.canDeletePerson) {
            return true;
        }

        if(!this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection()) {
            return false;
        }
        else {
            if(this.dataGridVM.getSelectedRowsCollection().length > 0) {
                return false;
            }

            return !this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection() || this.dataGridVM.getSelectedRowsCollection().length <= 0;
        }
    }

    @computedFrom(Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM),
        Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM.toggleAll),
        Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM.itemsCollection),
        Reflection.getPropertyPath<ViewPeople>(z => z.dataGridVM.itemsCollection.length),
        Reflection.getPropertyPath<ViewPeople>(z => z.canEditPerson),
        "dataGridVM._specificIds", "dataGridVM._specificIds.length")
    get shouldDisableBulkChangeRole(): boolean {
        if(!this.canEditPerson || !this.dataGridVM) {
            return true;
        }

        //Check if user is toggling all then check if there are any items in the grid
        if(this.dataGridVM.toggleAll) {
            if (!this.dataGridVM.itemsCollection || this.dataGridVM.itemsCollection.length <= 0) {
                return true;
            }
            return false;
        }
        else {
            if (!this.dataGridVM.getSelectedRowsCollection() || !this.dataGridVM.getSpecificIds()) {
                return true;
            }
            else {
                if(this.dataGridVM.getSelectedRowsCollection().length > 0 || this.dataGridVM.getSpecificIds().length > 0) {
                    return false;
                }
    
                return !this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection() || !this.dataGridVM.getSpecificIds() 
                    || this.dataGridVM.getSelectedRowsCollection().length <= 0 || this.dataGridVM.getSpecificIds().length <= 0;
            }
        }
    }

    showToggledActivityDialog(isActivating: boolean) {

        const items = this.dataGridVM.getSelectedRowsCollection().map(x => x.item);

        if(items.length > 0) {
            
             if ((isActivating && this.shouldDisableActivate) ||
                 (!isActivating && this.shouldDisableDeactivate)) return;

            const model = new RightshipConfirmationDialogModel();
            model.confirm = isActivating ? "Activate" : "Deactivate";
            model.cancel = "Cancel";

            if (isActivating) {
                model.messages.push('Selected user(s) will be activated. These users will be able to access Rightship Platform.');
                model.title = "Confirm Activate Users";
            }
            else {            
                model.messages.push('Selected user(s) will be deactivated. These users will not be able to access Rightship Platform but will remain in the people listing.');
                model.title = "Confirm Deactivate Users";
            }

            model.onConfirm = () => {
                this.toggleUserActivity(items);
            }

            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, true, false);
        }
    }

    moveMultiplePersonsToCustomer() {
        const items = this.dataGridVM.getSelectedRowsCollection().map(x => x.item);

        if(items.length > 0){
            if (this.shouldDisableMove) return;
            
            this.movePersonToCustomer(items);
        }
    }

    deleteDialogMultiplePersons() {
        const items = this.dataGridVM.getSelectedRowsCollection().map(x => x.item);

        if(items.length > 0) {
            if (this.shouldDisableDelete) return;
            
            const model = new RightshipConfirmationDialogModel();
            model.confirm = "Yes, Remove user";
            model.cancel = "Cancel";
            model.confirmClass = "danger";

            model.messages.push('Selected user(s) will be removed. These users will not be able to access Rightship Platform.');
            model.title = "Confirm Remove User(s)";

            model.onConfirm = () => {
                this.deleteUsers(items.map(x => x.userId));
            }

            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, true, false);
        }
    }

    public applyColumnSortingFilter(items: any[], reload: boolean = true) {
        this.selectedColumnSorting = items.map(x => x.id);
        if (reload == true) this.setPeopleItemSource();
        this.toggleColumnSortingFilters();
    }

    downloadExcel(){
        const columns = [<DataGridColumn>
            {
                dataType: DataType.Default,
                name: "fullName",
                title: "Name",
                columnOrder: 1,
                isHidden: false
            },<DataGridColumn>
            {
                dataType: DataType.Default,
                name: "email",
                title: "Email",
                columnOrder: 1,
                isHidden: false
            },<DataGridColumn>
            {
                dataType: DataType.Default,
                name: "phoneNumber",
                title: "Phone Number",
                columnOrder: 1,
                isHidden: false
            },<DataGridColumn>
            {
                dataType: DataType.Default,
                name: "businessUnitName",
                title: "Business Unit",
                columnOrder: 1,
                isHidden: false
            },<DataGridColumn>
            {
                dataType: DataType.Default,
                name: "businessUnitAccess",
                title: "Business Unit Access",
                columnOrder: 1,
                isHidden: false
            },<DataGridColumn>
            {
                dataType: DataType.Default,
                name: "roleName",
                title: "Role",
                columnOrder: 1,
                isHidden: false
            },<DataGridColumn>
            {
                dataType: DataType.Boolean,
                name: "canAmmendVetRules",
                title: "Authorised to amend vetting rules",
                columnOrder: 1,
                isHidden: false
            },<DataGridColumn>
            {
                dataType: DataType.Boolean,
                name: "isActive",
                title: "Is Active",
                columnOrder: 1,
                isHidden: false
            },<DataGridColumn>
            {
                dataType: DataType.Boolean,
                name: "isUserMfaExcluded",
                title: "MFA Excluded",
                columnOrder: 1,
                isHidden: false
            }]
        
        this.dataGridVM.excelName = "RightShip Platform Users";
        this.dataGridVM.downloadExcel(columns);
    }

    showUserAuditDialog(userId: string){
        const model = { userId: userId, entityType: EntityTypes.User};
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.UserAuditDialog, model, true, true, false, 'right');
    }
}